import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { DeletedUserBadge } from "@/components/DeletedUserBadge";
import { toast } from "@/components/Toast";
import { useAuth } from "@/contexts/AuthContext";
import { AttachmentPicker, AttachmentGrid } from "@/components/AttachmentPicker";
import { ArrowLeft, Fuel, Boxes, Wallet, Trash2, Gauge, Plus, CheckCircle2, AlertTriangle, XCircle, MapPin, Pencil, Download } from "lucide-react";

const MONTH_LABELS = { "01": "Ene", "02": "Feb", "03": "Mar", "04": "Abr", "05": "May", "06": "Jun", "07": "Jul", "08": "Ago", "09": "Sep", "10": "Oct", "11": "Nov", "12": "Dic" };
const periodLabel = (p) => {
  if (!p || !p.includes("-")) return p || "—";
  const [y, m] = p.split("-");
  return `${MONTH_LABELS[m] || m} ${y}`;
};

const CATEGORIES = [
  { key: "baterias_respaldo", label: "Baterías / Respaldo" },
  { key: "materiales_tacticos", label: "Materiales Tácticos" },
  { key: "materiales_oficina", label: "Materiales de Oficina" },
  { key: "equipamiento_tecnico", label: "Equipamiento Técnico del Puesto" },
];
const catLabel = (k) => CATEGORIES.find((c) => c.key === k)?.label || k;

const STATUS = {
  ok: { label: "OK", color: "#16a34a", Ic: CheckCircle2 },
  falla: { label: "Falla", color: "#d97706", Ic: AlertTriangle },
  faltante: { label: "Faltante", color: "#BF3437", Ic: XCircle },
};

export default function Logistics() {
  const { user } = useAuth();
  const backLink = user?.role === "admin" ? "/admin" : "/supervisor";
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "fuel");
  const [clients, setClients] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const changeTab = (t) => { setTab(t); setSearchParams(t === "fuel" ? {} : { tab: t }); };

  useEffect(() => { (async () => {
    try {
      const [c, s] = await Promise.all([api.get("/clients"), api.get("/users/roster?role=supervisor")]);
      setClients(c.data); setSupervisors(s.data);
    }
    catch (e) { toast.error(formatApiError(e)); }
  })(); }, []);

  return (
    <div style={{ padding: "20px 32px", maxWidth: 1200, margin: "0 auto" }} data-testid="logistics-page">
      <Link to={backLink} style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--invict-text-muted)", fontSize: 12, marginBottom: 12, textDecoration: "none", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.05em" }}><ArrowLeft size={14}/> Volver</Link>
      <h1 style={{ fontSize: 26, fontWeight: 900, margin: "4px 0 4px", color: "#0A0A0A", letterSpacing: "-0.02em" }}>Logística / Recursos</h1>
      <p style={{ fontSize: 12, color: "var(--invict-text-muted)", marginBottom: 18 }}>Activos a nivel empresa. Distinto de Materiales (handoff por turno) y Elementos de Trabajo (kit del vigilador).</p>

      <div style={{ display: "flex", gap: 8, marginBottom: 18, flexWrap: "wrap" }}>
        <TabBtn active={tab === "fuel"} onClick={() => changeTab("fuel")} Ic={Fuel} label="Control de combustible" testid="logistics-tab-fuel"/>
        <TabBtn active={tab === "stock"} onClick={() => changeTab("stock")} Ic={Boxes} label="Auditoría de stock" testid="logistics-tab-stock"/>
        <TabBtn active={tab === "viaticos"} onClick={() => changeTab("viaticos")} Ic={Wallet} label="Viáticos" testid="logistics-tab-viaticos"/>
      </div>

      {tab === "fuel" ? <FuelModule clients={clients} supervisors={supervisors}/> : tab === "stock" ? <StockModule clients={clients}/> : <ViaticosModule supervisors={supervisors} role={user?.role}/>}
    </div>
  );
}

function TabBtn({ active, onClick, Ic, label, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "11px 18px", borderRadius: 8,
        border: "none", background: active ? "#0A0A0A" : "#fff", color: active ? "#FFFBF4" : "#0A0A0A",
        fontWeight: 800, fontSize: 13, cursor: "pointer", boxShadow: "0 1px 3px rgba(0,0,0,0.08)" }}>
      <Ic size={16}/> {label}
    </button>
  );
}

/* ============================ COMBUSTIBLE ============================ */
function FuelModule({ clients, supervisors }) {
  const empty = { plate: "", filled_at: new Date().toISOString().slice(0, 10), liters: "", amount: "", odometer: "", client_id: "", supervisor_id: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState([]);
  const [busy, setBusy] = useState(false);
  const [filterPlate, setFilterPlate] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refresh = async () => {
    try {
      const [l, s] = await Promise.all([api.get("/fuel-logs"), api.get("/fuel-logs/stats")]);
      setLogs(l.data); setStats(s.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); }, []);

  const plateOptions = useMemo(() => Array.from(new Set(stats.map((s) => s.plate))), [stats]);
  const shownLogs = filterPlate ? logs.filter((l) => l.plate === filterPlate) : logs;

  const submit = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      await api.post("/fuel-logs", {
        plate: form.plate, filled_at: form.filled_at,
        liters: Number(form.liters),
        amount: form.amount === "" ? null : Number(form.amount),
        odometer: form.odometer === "" ? null : Number(form.odometer),
        client_id: form.client_id || null, supervisor_id: form.supervisor_id || null, notes: form.notes || null,
      });
      toast.success("Carga registrada");
      setForm({ ...empty, plate: form.plate, client_id: form.client_id, supervisor_id: form.supervisor_id });
      refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    try { await api.delete(`/fuel-logs/${id}`); toast.success("Carga eliminada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="fuel-module">
      <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="fuel-form">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <div className="field"><label>Patente *</label>
            <input required list="fuel-plates" value={form.plate} onChange={(e) => set("plate", e.target.value.toUpperCase())} placeholder="AA123BB" data-testid="fuel-plate"/>
            <datalist id="fuel-plates">{plateOptions.map((p) => <option key={p} value={p}/>)}</datalist>
          </div>
          <div className="field"><label>Fecha *</label><input required type="date" value={form.filled_at} onChange={(e) => set("filled_at", e.target.value)} data-testid="fuel-date"/></div>
          <div className="field"><label>Litros *</label><input required type="number" step="0.01" min="0" value={form.liters} onChange={(e) => set("liters", e.target.value)} data-testid="fuel-liters"/></div>
          <div className="field"><label>Monto ($)</label><input type="number" step="0.01" min="0" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="fuel-amount"/></div>
          <div className="field"><label>Odómetro (km)</label><input type="number" min="0" value={form.odometer} onChange={(e) => set("odometer", e.target.value)} placeholder="ej. 10850" data-testid="fuel-odometer"/></div>
          <div className="field"><label>Objetivo (opcional)</label>
            <select value={form.client_id} onChange={(e) => set("client_id", e.target.value)} data-testid="fuel-client">
              <option value="">—</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Supervisor (opcional)</label>
            <select value={form.supervisor_id} onChange={(e) => set("supervisor_id", e.target.value)} data-testid="fuel-supervisor">
              <option value="">—</option>
              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Notas</label><input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="fuel-notes"/></div>
        <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="fuel-submit">{busy ? "Registrando..." : "Registrar carga"}</button>
      </form>

      {/* Métricas de consumo por vehículo */}
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>Consumo por vehículo</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))", gap: 12, marginBottom: 22 }} data-testid="fuel-stats">
        {stats.length === 0 && <div style={{ color: "var(--invict-text-muted)", fontSize: 13 }}>Sin datos de consumo aún.</div>}
        {stats.map((s) => (
          <div key={s.plate} className="card" style={{ padding: 14, cursor: "pointer", borderLeft: `4px solid ${filterPlate === s.plate ? "#BF3437" : "transparent"}` }}
            onClick={() => setFilterPlate(filterPlate === s.plate ? "" : s.plate)} data-testid={`fuel-stat-${s.plate}`}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
              <Gauge size={16} color="#BF3437"/><span style={{ fontWeight: 900, fontSize: 15 }}>{s.plate}</span>
            </div>
            <Metric label="Consumo" value={s.km_per_liter != null ? `${s.km_per_liter} km/l` : "—"}/>
            <Metric label="L/100km" value={s.liters_per_100km != null ? `${s.liters_per_100km}` : "—"}/>
            <Metric label="Autonomía estim." value={s.autonomy_km != null ? `${s.autonomy_km} km` : "—"}/>
            <Metric label="Cargas / Litros" value={`${s.fills} · ${s.total_liters} L`}/>
            <Metric label="Gasto total" value={s.total_amount ? `$${s.total_amount.toLocaleString("es-AR")}` : "—"}/>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A" }}>Historial de cargas {filterPlate && `· ${filterPlate}`}</div>
        {filterPlate && <button onClick={() => setFilterPlate("")} style={{ background: "none", border: "none", color: "#BF3437", fontSize: 12, fontWeight: 700, cursor: "pointer" }} data-testid="fuel-clear-filter">Ver todas</button>}
      </div>
      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="fuel-list">
        {shownLogs.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin cargas registradas.</div>}
        {shownLogs.map((l) => (
          <div key={l.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center" }} data-testid={`fuel-row-${l.id}`}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{l.plate} · {l.liters} L {l.amount ? `· $${l.amount.toLocaleString("es-AR")}` : ""}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{l.filled_at}{l.odometer != null ? ` · ${l.odometer.toLocaleString("es-AR")} km` : ""}{l.client_name ? ` · ${l.client_name}` : ""}{l.supervisor_name ? ` · ${l.supervisor_name}` : ""}{l.supervisor_deleted && <DeletedUserBadge id={l.id} />} · por {l.created_by_name}</div>
              {l.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{l.notes}</div>}
            </div>
            <button onClick={() => del(l.id)} style={{ border: "none", background: "transparent", color: "#BF3437", cursor: "pointer" }} data-testid={`fuel-delete-${l.id}`}><Trash2 size={16}/></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "2px 0" }}>
      <span style={{ color: "var(--invict-text-muted)" }}>{label}</span>
      <span style={{ fontWeight: 800, color: "#0A0A0A" }}>{value}</span>
    </div>
  );
}

/* ============================ STOCK ============================ */
function StockModule({ clients }) {
  const empty = { name: "", category: "baterias_respaldo", quantity: "", status: "ok", location_client_id: "", notes: "" };
  const [form, setForm] = useState(empty);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fCat, setFCat] = useState("");
  const [fStatus, setFStatus] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refresh = async () => {
    try {
      const params = [];
      if (fCat) params.push(`category=${fCat}`);
      if (fStatus) params.push(`status=${fStatus}`);
      const r = await api.get(`/supplies${params.length ? "?" + params.join("&") : ""}`);
      setItems(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [fCat, fStatus]);

  const submit = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      await api.post("/supplies", {
        name: form.name, category: form.category,
        quantity: form.quantity === "" ? 0 : Number(form.quantity),
        status: form.status, location_client_id: form.location_client_id || null,
        notes: form.notes || null,
      });
      toast.success("Ítem agregado al inventario");
      setForm({ ...empty, category: form.category });
      refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const audit = async (id, patch) => {
    try { await api.patch(`/supplies/${id}`, patch); toast.success("Auditoría registrada"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/supplies/${id}`); toast.success("Ítem eliminado"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="stock-module">
      <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="stock-form">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <div className="field"><label>Ítem *</label><input required value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="ej. Batería UPS APC" data-testid="stock-name"/></div>
          <div className="field"><label>Categoría *</label>
            <select value={form.category} onChange={(e) => set("category", e.target.value)} data-testid="stock-category">
              {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
            </select>
          </div>
          <div className="field"><label>Cantidad</label><input type="number" min="0" value={form.quantity} onChange={(e) => set("quantity", e.target.value)} data-testid="stock-quantity"/></div>
          <div className="field"><label>Estado</label>
            <select value={form.status} onChange={(e) => set("status", e.target.value)} data-testid="stock-status">
              {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div className="field"><label>Ubicación / Puesto</label>
            <select value={form.location_client_id} onChange={(e) => set("location_client_id", e.target.value)} data-testid="stock-location">
              <option value="">— Depósito central —</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        </div>
        <div className="field"><label>Notas</label><input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="stock-notes"/></div>
        <button type="submit" disabled={busy} className="btn-primary" style={{ width: "100%", padding: 12 }} data-testid="stock-submit"><Plus size={14} style={{ display: "inline", marginRight: 6 }}/>{busy ? "Agregando..." : "Agregar al inventario"}</button>
      </form>

      {/* Filtros */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }} data-testid="stock-filters">
        <select value={fCat} onChange={(e) => setFCat(e.target.value)} data-testid="stock-filter-category" style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--invict-bone-3)", fontWeight: 700, fontSize: 12 }}>
          <option value="">Todas las categorías</option>
          {CATEGORIES.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select value={fStatus} onChange={(e) => setFStatus(e.target.value)} data-testid="stock-filter-status" style={{ padding: "8px 12px", borderRadius: 8, border: "1px solid var(--invict-bone-3)", fontWeight: 700, fontSize: 12 }}>
          <option value="">Todos los estados</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="stock-list">
        {items.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin ítems en el inventario.</div>}
        {items.map((it) => {
          const st = STATUS[it.status] || STATUS.ok;
          const StIc = st.Ic;
          return (
            <div key={it.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid={`stock-row-${it.id}`}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{it.name}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, background: "var(--invict-bone-3)", color: "#0A0A0A", padding: "2px 7px", borderRadius: 10 }}>{catLabel(it.category)}</span>
                  <span style={{ fontSize: 10, fontWeight: 800, color: "#fff", background: st.color, padding: "2px 8px", borderRadius: 10, display: "inline-flex", alignItems: "center", gap: 4 }} data-testid={`stock-badge-${it.id}`}><StIc size={11}/>{st.label}</span>
                </div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>
                  Cantidad: <b>{it.quantity}</b>{it.location_name ? <> · <MapPin size={10} style={{ display: "inline" }}/> {it.location_name}</> : " · Depósito central"}
                </div>
                <div style={{ fontSize: 10, color: "var(--invict-text-muted)" }}>Últ. verificación: {it.last_checked_by_name} · {it.last_checked_at ? new Date(it.last_checked_at).toLocaleString("es-AR") : "—"}</div>
                {it.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{it.notes}</div>}
              </div>
              {/* Auditoría rápida */}
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <input type="number" min="0" defaultValue={it.quantity} style={{ width: 64, padding: "6px 8px", borderRadius: 6, border: "1px solid var(--invict-bone-3)", fontSize: 12 }}
                  onBlur={(e) => { const q = Number(e.target.value); if (q !== it.quantity) audit(it.id, { quantity: q }); }}
                  data-testid={`stock-audit-qty-${it.id}`}/>
                <select defaultValue={it.status} onChange={(e) => audit(it.id, { status: e.target.value })} style={{ padding: "6px 8px", borderRadius: 6, border: "1px solid var(--invict-bone-3)", fontSize: 12, fontWeight: 700 }} data-testid={`stock-audit-status-${it.id}`}>
                  {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
                <button onClick={() => del(it.id)} style={{ border: "none", background: "transparent", color: "#BF3437", cursor: "pointer" }} data-testid={`stock-delete-${it.id}`}><Trash2 size={16}/></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}


/* ============================ VIÁTICOS ============================ */
function ViaticosModule({ supervisors, role }) {
  const isAdmin = role === "admin";
  const thisYear = new Date().getFullYear();
  const empty = { supervisor_id: "", period: new Date().toISOString().slice(0, 7), concept: "", amount: "", payment_date: new Date().toISOString().slice(0, 10), payment_method: "", notes: "", attachment: null };
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [items, setItems] = useState([]);
  const [totalPeriod, setTotalPeriod] = useState(0);
  const [bySupervisor, setBySupervisor] = useState([]);
  const [busy, setBusy] = useState(false);
  const [fSupervisor, setFSupervisor] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const refresh = async () => {
    try {
      const params = [];
      if (isAdmin && fSupervisor) params.push(`supervisor_id=${fSupervisor}`);
      if (isAdmin && fFrom) params.push(`date_from=${fFrom}`);
      if (isAdmin && fTo) params.push(`date_to=${fTo}`);
      const r = await api.get(`/viaticos${params.length ? "?" + params.join("&") : ""}`);
      setItems(r.data.items); setTotalPeriod(r.data.total_period); setBySupervisor(r.data.by_supervisor);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  // eslint-disable-next-line
  useEffect(() => { refresh(); }, [fSupervisor, fFrom, fTo]);

  // Supervisor: al abrir esta solapa, los avisos no vistos dejan de mostrarse.
  useEffect(() => {
    if (isAdmin) return;
    (async () => {
      try {
        const r = await api.get("/viaticos/unseen");
        await Promise.all(r.data.map((v) => api.post(`/viaticos/${v.id}/seen`)));
      } catch { /* no bloquea la vista */ }
    })();
  }, [isAdmin]);

  const totalYear = useMemo(() => items.filter((i) => (i.payment_date || "").startsWith(String(thisYear))).reduce((s, i) => s + i.amount, 0), [items, thisYear]);

  const submit = async (e) => {
    e.preventDefault(); if (busy) return; setBusy(true);
    try {
      const payload = {
        supervisor_id: form.supervisor_id, period: form.period, concept: form.concept,
        amount: Number(form.amount), payment_date: form.payment_date,
        payment_method: form.payment_method || null, notes: form.notes || null,
        attachment: form.attachment,
      };
      if (editingId) { await api.patch(`/viaticos/${editingId}`, payload); toast.success("Pago actualizado"); }
      else { await api.post("/viaticos", payload); toast.success("Pago registrado"); }
      setForm(empty); setEditingId(null); refresh();
    } catch (e2) { toast.error(formatApiError(e2)); }
    finally { setBusy(false); }
  };

  const startEdit = (v) => {
    setEditingId(v.id);
    setForm({ supervisor_id: v.supervisor_id, period: v.period, concept: v.concept, amount: String(v.amount), payment_date: v.payment_date, payment_method: v.payment_method || "", notes: v.notes || "", attachment: v.attachment || null });
  };
  const cancelEdit = () => { setEditingId(null); setForm(empty); };

  const del = async (id) => {
    if (!window.confirm("¿Eliminar este pago de viático?")) return;
    try { await api.delete(`/viaticos/${id}`); toast.success("Pago eliminado"); refresh(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const exportFile = async (kind) => {
    try {
      const params = [];
      if (fSupervisor) params.push(`supervisor_id=${fSupervisor}`);
      if (fFrom) params.push(`date_from=${fFrom}`);
      if (fTo) params.push(`date_to=${fTo}`);
      const res = await api.get(`/viaticos/export.${kind}${params.length ? "?" + params.join("&") : ""}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = url; a.download = `viaticos.${kind}`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!isAdmin) {
    return (
      <div data-testid="viaticos-module">
        <div className="card" style={{ padding: 16, marginBottom: 18, borderLeft: "4px solid #BF3437" }} data-testid="viatico-total-year">
          <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "var(--invict-text-muted)" }}>Total cobrado en {thisYear}</div>
          <div style={{ fontSize: 26, fontWeight: 900, color: "#0A0A0A", marginTop: 4 }}>${totalYear.toLocaleString("es-AR")}</div>
        </div>
        <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>Mis viáticos</div>
        <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="viatico-list">
          {items.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Todavía no tenés pagos de viáticos registrados.</div>}
          {items.map((v) => (
            <div key={v.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid={`viatico-row-${v.id}`}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{v.concept} · ${v.amount.toLocaleString("es-AR")}</div>
                <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{periodLabel(v.period)} · Pagado el {v.payment_date}{v.payment_method ? ` · ${v.payment_method}` : ""}</div>
                {v.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{v.notes}</div>}
              </div>
              {v.attachment && <AttachmentGrid items={[v.attachment]}/>}
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div data-testid="viaticos-module">
      <form onSubmit={submit} className="card" style={{ padding: 16, marginBottom: 18 }} data-testid="viatico-form">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10 }}>
          <div className="field"><label>Supervisor *</label>
            <select required value={form.supervisor_id} onChange={(e) => set("supervisor_id", e.target.value)} data-testid="viatico-supervisor">
              <option value="">Elegir...</option>
              {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="field"><label>Período *</label><input required type="month" value={form.period} onChange={(e) => set("period", e.target.value)} data-testid="viatico-period"/></div>
          <div className="field"><label>Concepto *</label><input required value={form.concept} onChange={(e) => set("concept", e.target.value)} placeholder="ej. Viáticos septiembre" data-testid="viatico-concept"/></div>
          <div className="field"><label>Monto ($) *</label><input required type="number" step="0.01" min="0.01" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="viatico-amount"/></div>
          <div className="field"><label>Fecha de pago *</label><input required type="date" value={form.payment_date} onChange={(e) => set("payment_date", e.target.value)} data-testid="viatico-payment-date"/></div>
          <div className="field"><label>Medio de pago</label><input value={form.payment_method} onChange={(e) => set("payment_method", e.target.value)} placeholder="ej. Transferencia, efectivo" data-testid="viatico-payment-method"/></div>
        </div>
        <div className="field"><label>Notas</label><input value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="viatico-notes"/></div>
        <div className="field">
          <label>Comprobante (opcional)</label>
          <AttachmentPicker accept="image_document" multiple={false} max={1} label="Adjuntar comprobante"
            value={form.attachment ? [form.attachment] : []} onChange={(list) => set("attachment", list[0] || null)}/>
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
          <button type="submit" disabled={busy} className="btn-primary" style={{ flex: 1, padding: 12 }} data-testid="viatico-submit">{busy ? "Guardando..." : editingId ? "Guardar cambios" : "Registrar pago"}</button>
          {editingId && <button type="button" onClick={cancelEdit} className="btn-ghost" style={{ padding: 12 }} data-testid="viatico-cancel-edit">Cancelar</button>}
        </div>
      </form>

      {/* Totales */}
      <div style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em", color: "#0A0A0A", marginBottom: 10 }}>Total pagado en el período</div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: 12, marginBottom: 22 }}>
        <div className="card" style={{ padding: 14, borderLeft: "4px solid #BF3437" }} data-testid="viatico-total-period">
          <div style={{ fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 700, textTransform: "uppercase" }}>Total del período</div>
          <div style={{ fontSize: 22, fontWeight: 900, color: "#0A0A0A", marginTop: 4 }}>${totalPeriod.toLocaleString("es-AR")}</div>
        </div>
        {bySupervisor.map((s) => (
          <div key={s.supervisor_id} className="card" style={{ padding: 14, cursor: "pointer", borderLeft: `4px solid ${fSupervisor === s.supervisor_id ? "#BF3437" : "transparent"}` }}
            onClick={() => setFSupervisor(fSupervisor === s.supervisor_id ? "" : s.supervisor_id)} data-testid={`viatico-stat-${s.supervisor_id}`}>
            <div style={{ fontSize: 10, color: "var(--invict-text-muted)", fontWeight: 700, textTransform: "uppercase" }}>{s.supervisor_name}</div>
            <div style={{ fontSize: 18, fontWeight: 900, color: "#0A0A0A", marginTop: 4 }}>${s.total.toLocaleString("es-AR")}</div>
            <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{s.count} pago(s)</div>
          </div>
        ))}
      </div>

      {/* Filtros + export */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "end", marginBottom: 12 }}>
        <div className="field" style={{ minWidth: 160 }}><label>Supervisor</label>
          <select value={fSupervisor} onChange={(e) => setFSupervisor(e.target.value)} data-testid="viatico-filter-supervisor">
            <option value="">Todos</option>
            {supervisors.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <div className="field"><label>Desde</label><input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="viatico-filter-from"/></div>
        <div className="field"><label>Hasta</label><input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="viatico-filter-to"/></div>
        <button type="button" className="btn-ghost" onClick={() => exportFile("csv")} style={{ padding: "9px 14px" }} data-testid="viatico-export-csv"><Download size={13} style={{ marginRight: 6 }}/>Exportar CSV</button>
        <button type="button" className="btn-ghost" onClick={() => exportFile("pdf")} style={{ padding: "9px 14px" }} data-testid="viatico-export-pdf"><Download size={13} style={{ marginRight: 6 }}/>Exportar PDF</button>
      </div>

      <div className="card" style={{ padding: 0, overflow: "hidden" }} data-testid="viatico-list">
        {items.length === 0 && <div style={{ padding: 30, textAlign: "center", color: "var(--invict-text-muted)", fontSize: 13 }}>Sin pagos de viáticos registrados.</div>}
        {items.map((v) => (
          <div key={v.id} style={{ padding: "12px 16px", borderBottom: "1px solid var(--invict-bone-3)", display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }} data-testid={`viatico-row-${v.id}`}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: "#0A0A0A" }}>{v.supervisor_name}{v.supervisor_deleted && <DeletedUserBadge id={v.id} />} · {v.concept} · ${v.amount.toLocaleString("es-AR")}</div>
              <div style={{ fontSize: 11, color: "var(--invict-text-muted)" }}>{periodLabel(v.period)} · Pagado el {v.payment_date}{v.payment_method ? ` · ${v.payment_method}` : ""} · por {v.created_by_name} el {new Date(v.created_at).toLocaleDateString("es-AR")}</div>
              {v.notes && <div style={{ fontSize: 11, color: "var(--invict-text-muted)", fontStyle: "italic" }}>{v.notes}</div>}
              {v.attachment && <AttachmentGrid items={[v.attachment]}/>}
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button onClick={() => startEdit(v)} style={{ border: "none", background: "transparent", color: "#0A0A0A", cursor: "pointer" }} data-testid={`viatico-edit-${v.id}`}><Pencil size={16}/></button>
              <button onClick={() => del(v.id)} style={{ border: "none", background: "transparent", color: "#BF3437", cursor: "pointer" }} data-testid={`viatico-delete-${v.id}`}><Trash2 size={16}/></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
